/* eslint-disable no-await-in-loop, no-restricted-syntax */
import { parsePageOutput } from 'services/imports/extractWithAssistant';
import type { PageLines, PageNotice } from 'services/imports/extractWithAssistant';
import { buildAmendmentPrompt } from 'services/imports/buildAmendmentPrompt';
import type { ExtractedFact } from 'services/imports/buildAmendmentPrompt';
import type { ImportPage } from 'services/imports/fileToPages';
import type { LineSource, LineStatus } from 'services/imports/markdownContract';

export type AmendmentSuggestion = {
  id: string;
  pageId: string;
  page: { file: string; page: number };
  key: string;
  value: string;
  src: LineSource | null;
  status: LineStatus;
};

export type ReviewAmendmentsInput = {
  pages: readonly ImportPage[];
  lines: PageLines;
  extractedByPage: ReadonlyMap<string, readonly ExtractedFact[]>;
  reviewImage: (prompt: string, imageDataUrl: string) => Promise<string>;
};

export type ReviewAmendmentsResult = {
  amendments: AmendmentSuggestion[];
  errors: PageNotice[];
};

const toErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

export const reviewAmendments = async ({ pages, lines, extractedByPage, reviewImage }: ReviewAmendmentsInput): Promise<ReviewAmendmentsResult> => {
  const amendments: AmendmentSuggestion[] = [];
  const errors: PageNotice[] = [];

  for (const page of pages) {
    const pageLines = lines.get(page.id) ?? [];
    if (!page.failed) {
      try {
        const prompt = buildAmendmentPrompt({
          fileName: page.fileName,
          pageNumber: page.pageNumber,
          lines: pageLines,
          extracted: extractedByPage.get(page.id) ?? [],
        });
        const output = await reviewImage(prompt, page.imageDataUrl);
        parsePageOutput(output, page, pageLines)
          .filter((line) => line.status !== 'malformed' && line.key !== '')
          .forEach((line, index) => {
            amendments.push({
              id: `${page.id}:${index}`,
              pageId: page.id,
              page: { file: page.fileName, page: page.pageNumber },
              key: line.key,
              value: line.value,
              // A citation to a line that is not on the page is dropped; the amendment keeps its unverified status.
              src: line.status === 'unverified' ? null : line.src,
              status: line.status,
            });
          });
      } catch (error) {
        errors.push({ pageId: page.id, message: toErrorMessage(error) });
      }
    }
  }

  return { amendments, errors };
};
